import { useCallback, useEffect, useRef } from "react";

import { WELL_LAYER, XDA_PROJECTION_LAYER } from "constants/mapLayers.constants";
import _debounce from "lodash/debounce";
import mapboxgl from "mapbox-gl";

import { MapLoadingSource } from "../contexts/MapContextState";
import useGetXdaProjection from "./useGetXdaProjection";
import { useMapContext } from "./useMapContext";
import { useMapDispatch } from "./useMapDispatch";

export interface XdaProjectionLayerProps {
  txnId: Maybe<string>;
  canFetch: boolean;
  removeLayer: (layerId: string) => void;
  addLayer: (layer: mapboxgl.AnyLayer, beforeId?: string) => void;
}

/**
 * Hook that manages the XDA projection layer on the map.
 *
 * This hook assumes it is only called when XDA projection is active.
 * Mount/unmount the consuming component to control the lifecycle —
 * the hook cleans up the layer and loading state on unmount.
 */
const useXdaProjectionLayer = (options: XdaProjectionLayerProps) => {
  const { txnId, canFetch, removeLayer, addLayer } = options;

  const isMountedRef = useRef(true);
  const mapDispatch = useMapDispatch();
  const { baseLayer, mapbox } = useMapContext();

  const {
    data: xdaProjectionData,
    isLoading,
    isPreviousData,
    dataUpdatedAt
  } = useGetXdaProjection({
    showXdaProjection: true,
    txnId,
    canFetch
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const removeXdaProjectionLayer = useCallback(() => {
    if (!mapbox) return;

    try {
      removeLayer(XDA_PROJECTION_LAYER);
      if (mapbox.getSource(XDA_PROJECTION_LAYER)) {
        mapbox.removeSource(XDA_PROJECTION_LAYER);
      }
    } catch (err) {
      // Layer might not exist, ignore
    }
  }, [mapbox, removeLayer]);

  // Forward loading state to map context
  useEffect(() => {
    if (isMountedRef.current) {
      mapDispatch({
        payload: {
          isLoading,
          loadingSource: MapLoadingSource.XdaProjection
        }
      });
    }
  }, [isLoading, mapDispatch]);

  // Add / update the projection layer when data arrives.
  // Skip while isPreviousData: keepPreviousData keeps the prior txnId's
  // GeoJSON around during refetch so the query doesn't blank out, but that
  // data belongs to the wells from before the txnId change — draw nothing
  // rather than the wrong group's shapes until the new txnId's data lands.
  useEffect(() => {
    if (!xdaProjectionData || !mapbox) {
      return;
    }

    if (isPreviousData) {
      removeXdaProjectionLayer();
      return;
    }

    const debouncedUpdate = _debounce(() => {
      removeXdaProjectionLayer();

      mapbox.addSource(XDA_PROJECTION_LAYER, {
        type: "geojson",
        data: xdaProjectionData as GeoJSON.FeatureCollection
      });

      addLayer(
        {
          id: XDA_PROJECTION_LAYER,
          type: "fill",
          source: XDA_PROJECTION_LAYER,
          layout: {
            visibility: "visible"
          },
          paint: {
            "fill-color": ["get", "color"],
            "fill-outline-color": ["get", "color"],
            "fill-opacity": ["coalesce", ["get", "opacity"], 0.4]
          }
        } as mapboxgl.FillLayer,
        WELL_LAYER
      );
    }, 500);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [
    xdaProjectionData,
    dataUpdatedAt,
    isPreviousData,
    baseLayer,
    mapbox,
    addLayer,
    removeXdaProjectionLayer
  ]);

  // Clean up layer and loading state on unmount
  useEffect(() => {
    return () => {
      removeXdaProjectionLayer();
      mapDispatch({
        payload: {
          isLoading: false,
          loadingSource: MapLoadingSource.XdaProjection
        }
      });
    };
  }, [removeXdaProjectionLayer, mapDispatch]);
};

export default useXdaProjectionLayer;
